/**
 * CompletionBar — the contestant-facing application progress bar
 * (application-completion-tracker spec, design §C3).
 *
 * Rendered at the top of the editable "Your application" surface, above the
 * dynamic sections (Requirement 7.1). It is a token-only progress bar with a
 * visible, accessible label so the percentage is announced without relying on
 * colour alone (Requirements 7.5, 9.1, 9.2, 9.5).
 *
 *   - `availability === 'UNAVAILABLE'` ⇒ renders nothing (Requirement 8.2).
 *   - Otherwise a filled track whose fill width is `percent%` (Requirement 7.3)
 *     with `role="progressbar"`, `aria-valuenow/min/max` (Requirement 9.1) and a
 *     visible + accessible label — "Application {percent}% complete", or
 *     "No required fields" when `totalCount === 0` (Requirements 9.2, 4.2).
 *
 * Every colour comes from a design token (`bg-primary`, `bg-muted`, …); there
 * are no hex/rgb/hsl literals (Requirement 7.5, 9.5).
 *
 * Validates: Requirements 7.1, 7.2, 7.3, 7.5, 8.2, 9.1, 9.2, 9.5
 */

import * as React from 'react';

import { cn } from '@/lib/utils';
import type { CompletionResult } from '@/lib/application-completion/computeCompletion';

export interface CompletionBarProps {
  /** The computed completion for the applicant. */
  result: CompletionResult;
  /** Optional className passthrough. */
  className?: string;
}

/**
 * The contestant application progress bar. Renders nothing when completion is
 * unavailable; otherwise a labelled, accessible, token-only filled track.
 */
export function CompletionBar({ result, className }: CompletionBarProps): React.ReactElement | null {
  // Graceful degradation: omit the bar entirely when unavailable (Requirement 8.2).
  if (result.availability === 'UNAVAILABLE') {
    return null;
  }

  const { percent, totalCount } = result;
  const hasRequired = totalCount > 0;
  // Never colour-only: the label conveys the value (Requirements 9.2, 4.2).
  const label = hasRequired
    ? `Application ${percent}% complete`
    : 'No required fields';

  return (
    <div
      data-testid="application-completion-bar"
      className={cn('flex flex-col gap-1.5', className)}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-sans text-body-sm font-medium text-foreground">
          {label}
        </span>
      </div>
      <div
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label}
        data-testid="application-completion-bar-track"
        className="h-2 w-full overflow-hidden rounded-full bg-muted"
      >
        <div
          data-testid="application-completion-bar-fill"
          className="h-full rounded-full bg-primary transition-[width]"
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}

export default CompletionBar;
